import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Input, Progress } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useTheme } from '../../../contexts/ThemeContext';

// Função para calcular força da senha
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '#d9d9d9' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };
  
  // Calcular pontuação
  if (checks.length) score += 20;
  if (checks.uppercase) score += 20;
  if (checks.lowercase) score += 20;
  if (checks.numbers) score += 20;
  if (checks.special) score += 20;
  
  // Determinar nível e cor
  if (score < 40) {
    return { score, label: 'Muito fraca', color: '#ff4d4f' };
  } else if (score < 60) {
    return { score, label: 'Fraca', color: '#faad14' };
  } else if (score < 80) {
    return { score, label: 'Média', color: '#1890ff' };
  } else if (score < 100) {
    return { score, label: 'Forte', color: '#52c41a' };
  } else {
    return { score, label: 'Muito forte', color: '#389e0d' };
  }
};

const PasswordInput = ({
  value,
  onChange,
  placeholder,
  style,
  className,
  showStrengthIndicator = false,
  strengthIndicatorStyle = {},
  ...props
}) => {
  const { theme } = useTheme();
  const [focused, setFocused] = useState(false);

  // Calcular força da senha
  const strength = useMemo(() => {
    return calculatePasswordStrength(value);
  }, [value]);

  // Mostrar indicador apenas se habilitado e há valor ou campo está focado
  const shouldShowIndicator = showStrengthIndicator && (value || focused);

  return (
    <div style={{ width: '100%' }}>
      <Input.Password
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
        style={{
          backgroundColor: theme.inputBackground,
          color: theme.textSecondary,
          borderColor: theme.border,
          ...style
        }}
        className={className}
        {...props}
      />
      
      {shouldShowIndicator && (
        <div style={{ 
          marginTop: '8px', 
          fontSize: '12px',
          ...strengthIndicatorStyle 
        }}>
          <Progress
            percent={strength.score}
            strokeColor={strength.color}
            showInfo={false}
            size="small"
            style={{ marginBottom: '4px' }}
          />
          <div style={{ 
            color: strength.color, 
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Força da senha: {strength.label}</span>
            <span style={{ fontSize: '11px', color: theme.textSecondary }}>
              {strength.score}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

PasswordInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  showStrengthIndicator: PropTypes.bool,
  strengthIndicatorStyle: PropTypes.object,
};

export default PasswordInput; 